import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, downloadFile } from "@/api";
import { isUnavailableBrandColor, themeInputColor } from "@/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Eye, Ban, CheckCircle2, KeyRound, Shield, Copy, Trash2, ArchiveRestore,
  FileDown, Pencil, Building2, Wand2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const PLAN_STYLE = {
  Basic: "bg-muted text-muted-foreground border-border",
  Pro: "bg-info-tint text-info border-info/30",
  Enterprise: "bg-primary-tint text-primary border-primary/30",
};
const PLANS = { Basic: 999, Pro: 2499, Enterprise: 4999 };

const genPassword = () => {
  const s = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let p = "";
  for (let i = 0; i < 10; i++) p += s[Math.floor(Math.random() * s.length)];
  return p + "!";
};

export default function Platform() {
  const [overview, setOverview] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [delOpen, setDelOpen] = useState(null);
  const [permOpen, setPermOpen] = useState(null);
  const [permName, setPermName] = useState("");
  const [cloneOpen, setCloneOpen] = useState(null);
  const [cloneTarget, setCloneTarget] = useState("");
  const [templates, setTemplates] = useState([]);
  const emptyForm = { name: "", group: "client", industry_type: "service", template: "", owner_name: "", owner_email: "", owner_phone: "", owner_password: "", plan: "Basic", brand_color: themeInputColor("--primary-input-hex"), logo_url: "" };
  const [form, setForm] = useState(emptyForm);
  const [colorUnavailable, setColorUnavailable] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/overview").then((r) => setOverview(r.data));
    api.get("/platform/workspaces").then((r) => setWorkspaces(r.data));
    api.get("/platform/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/workspace/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (isUnavailableBrandColor(form.brand_color)) { setColorUnavailable(true); return; }
    try {
      const r = await api.post("/platform/tenants", { ...form, template: form.template || null });
      toast.success(`Workspace created — owner login: ${r.data.owner_email}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (isUnavailableBrandColor(editOpen.brand_color)) { setColorUnavailable(true); return; }
    try {
      await api.patch(`/platform/tenants/${editOpen.slug}`, { plan: editOpen.plan, brand_color: editOpen.brand_color });
      toast.success("Workspace updated"); setEditOpen(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (t, status) => { await api.patch(`/platform/tenants/${t.slug}`, { status }); toast.success(`${t.name} ${status}`); load(); };
  const resetPw = async (t) => {
    const pw = window.prompt(`New password for ${t.name}'s owner account:`);
    if (!pw || pw.length < 6) return;
    const r = await api.post(`/platform/tenants/${t.slug}/reset-password`, { password: pw });
    toast.success(`Password reset for ${r.data.email}`);
  };
  const openWorkspace = (tenant) => {
    localStorage.setItem("cm_active_tenant", tenant.id);
    localStorage.setItem("cm_last_tenant", tenant.id);
    sessionStorage.removeItem("cm_impersonating");
    window.location.assign("/admin");
  };
  const softDelete = async () => {
    try {
      const r = await api.post(`/platform/tenants/${delOpen.slug}/delete`);
      toast.success(`${delOpen.name} moved to recycle bin — restore within 30 days (by ${r.data.restore_by?.slice(0, 10)})`);
      setDelOpen(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const restore = async (t) => { await api.post(`/platform/tenants/${t.slug}/restore`); toast.success(`${t.name} restored`); load(); };
  const permanentDelete = async () => {
    try {
      const r = await api.post(`/platform/tenants/${permOpen.slug}/permanent-delete`, { confirm_name: permName });
      toast.success(`${permOpen.name} permanently deleted. Data archived to ${r.data.archived_to}`);
      setPermOpen(null); setPermName(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const cloneConfig = async () => {
    try {
      await api.post("/platform/clone-config", { source_slug: cloneOpen.slug, target_slug: cloneTarget });
      toast.success(`Configuration cloned ${cloneOpen.slug} → ${cloneTarget}`);
      setCloneOpen(null); setCloneTarget("");
    } catch (err) { toast.error(err.response?.data?.detail || "Clone failed"); }
  };

  const live = workspaces.filter((t) => t.status !== "deleted" && (!search || t.name.toLowerCase().includes(search.toLowerCase())));
  const bin = workspaces.filter((t) => t.status === "deleted");
  const groups = [["in_house", "In-House Companies"], ["client", "Client Companies"]];

  return (
    <div className="space-y-6" data-testid="platform-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Shield size={22} className="text-primary" /> Workspace Manager</h1>
          <p className="text-sm text-muted-foreground">All companies — in-house and client — one master console</p>
        </div>
        <Button data-testid="create-tenant-button" onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={16} className="mr-2" /> New Company</Button>
      </div>

      {overview && analytics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[["Workspaces", overview.total, "kpi-tenants"], ["In-House Revenue (mo)", inr(analytics.in_house_revenue_month), "kpi-inhouse-rev"],
              ["Client MRR", inr(analytics.client_mrr), "kpi-mrr"], ["Suspended", overview.suspended, "kpi-suspended"],
              ["Recycle Bin", bin.length, "kpi-bin"]].map(([l, v, tid]) => (
              <Card key={tid} className="p-5 rounded-2xl" data-testid={tid}>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
                <div className="font-mono text-2xl font-bold mt-2">{v}</div>
              </Card>
            ))}
          </div>
          <Card className="p-5 rounded-2xl" data-testid="chart-company-compare">
            <h3 className="font-heading font-semibold mb-3 text-sm">Per-Company Comparison (this month)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.per_company} margin={{ left: -14 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, n) => (n.includes("revenue") || n.includes("outstanding") ? inr(v) : v)} /><Legend />
                <Bar dataKey="revenue_month" name="Revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="outstanding" name="Outstanding" fill="hsl(var(--danger))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="leads_month" name="New Leads" fill="hsl(var(--info))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      <Input data-testid="tenant-search-input" placeholder="Search workspaces…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72 h-9" />

      {groups.map(([gkey, glabel]) => (
        <div key={gkey} className="space-y-3">
          <div className="flex items-center gap-2">
            <Building2 size={16} className={gkey === "in_house" ? "text-primary" : "text-info"} />
            <h2 className="font-heading font-semibold" data-testid={`group-${gkey}`}>{glabel}</h2>
            <span className="text-xs text-muted-foreground">({live.filter((t) => t.group === gkey).length})</span>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {live.filter((t) => t.group === gkey).map((t) => (
              <Card key={t.id} className="p-5 rounded-2xl hover:shadow-md transition-shadow" data-testid={`ws-card-${t.slug}`}>
                <div className="flex items-start gap-3">
                  {t.logo ? <img src={t.logo} alt="" className="w-10 h-10 rounded-xl object-cover border" />
                    : <div className="w-10 h-10 rounded-xl flex items-center justify-center text-inverse font-heading font-bold" style={{ background: t.brand_color || "hsl(var(--primary))" }}>{t.name[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold flex items-center gap-2">{t.name}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.status === "active" ? "bg-success-tint text-success border-success/30" : "bg-danger-tint text-danger border-danger/30"}`}>{t.status}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{t.industry_type} · {t.template || "custom"}{t.owner_name ? ` · ${t.owner_name}` : ""}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border ${PLAN_STYLE[t.plan] || ""}`}>{t.plan}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="rounded-lg bg-muted/50 py-2"><div className="font-mono font-bold text-sm">{t.users}</div><div className="text-[10px] text-muted-foreground uppercase">Users</div></div>
                  <div className="rounded-lg bg-muted/50 py-2"><div className="font-mono font-bold text-sm">{inr(t.revenue_month)}</div><div className="text-[10px] text-muted-foreground uppercase">Rev (mo)</div></div>
                  <div className="rounded-lg bg-muted/50 py-2"><div className="font-mono font-bold text-sm">{inr(t.outstanding)}</div><div className="text-[10px] text-muted-foreground uppercase">Due</div></div>
                </div>
                {gkey === "client" && <div className="text-[11px] text-muted-foreground mt-2">MRR {inr(PLANS[t.plan] || 0)}{t.last_login ? ` · last login ${t.last_login.slice(0, 10)}` : " · never logged in"}</div>}
                <div className="flex gap-1.5 mt-4 flex-wrap">
                  <Button size="sm" className="h-8 bg-info hover:bg-info" data-testid={`ws-open-${t.slug}`} onClick={() => openWorkspace(t)}><Eye size={13} className="mr-1" /> Open workspace</Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`ws-edit-${t.slug}`} onClick={() => setEditOpen({ ...t })} title="Edit"><Pencil size={13} /></Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`ws-resetpw-${t.slug}`} onClick={() => resetPw(t)} title="Reset password"><KeyRound size={13} /></Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`ws-clone-${t.slug}`} onClick={() => { setCloneOpen(t); setCloneTarget(""); }} title="Duplicate settings"><Copy size={13} /></Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`ws-export-${t.slug}`} onClick={() => downloadFile(`/platform/tenants/${t.slug}/export.zip`, `${t.slug}-export.zip`).then(() => toast.success("Export downloaded")).catch(() => toast.error("Export failed"))} title="Export data ZIP"><FileDown size={13} /></Button>
                  {t.status === "active"
                    ? <Button size="sm" variant="outline" className="h-8 text-danger-foreground border-danger" data-testid={`ws-suspend-${t.slug}`} onClick={() => setStatus(t, "suspended")} title="Suspend"><Ban size={13} /></Button>
                    : <Button size="sm" variant="outline" className="h-8 text-success border-success/40" data-testid={`ws-activate-${t.slug}`} onClick={() => setStatus(t, "active")} title="Activate"><CheckCircle2 size={13} /></Button>}
                  <Button size="sm" variant="outline" className="h-8 text-danger-foreground border-danger" data-testid={`ws-delete-${t.slug}`} onClick={() => setDelOpen(t)} title="Delete"><Trash2 size={13} /></Button>
                </div>
              </Card>
            ))}
            {!live.filter((t) => t.group === gkey).length && <div className="text-sm text-muted-foreground border border-dashed rounded-2xl p-6">No {glabel.toLowerCase()} yet.</div>}
          </div>
        </div>
      ))}

      {bin.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold flex items-center gap-2 text-danger-foreground"><Trash2 size={16} /> Recycle Bin <span className="text-xs text-muted-foreground font-normal">(auto-purge eligible after 30 days)</span></h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {bin.map((t) => (
              <Card key={t.id} className="p-5 rounded-2xl border-danger bg-danger" data-testid={`bin-card-${t.slug}`}>
                <div className="font-heading font-bold">{t.name}</div>
                <div className="text-xs text-muted-foreground">deleted {t.deleted_at?.slice(0, 10)} · restore by {t.restore_by?.slice(0, 10) || "—"}</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="h-8 text-success border-success/40" data-testid={`bin-restore-${t.slug}`} onClick={() => restore(t)}><ArchiveRestore size={13} className="mr-1" /> Restore</Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`bin-export-${t.slug}`} onClick={() => downloadFile(`/platform/tenants/${t.slug}/export.zip`, `${t.slug}-export.zip`)}><FileDown size={13} className="mr-1" /> Export ZIP</Button>
                  <Button size="sm" variant="outline" className="h-8 text-danger-foreground border-danger" data-testid={`bin-permdelete-${t.slug}`} onClick={() => { setPermOpen(t); setPermName(""); }}>Delete forever</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-tenant-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create New Company Workspace</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <Input data-testid="tenant-name-input" placeholder="Company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                <SelectTrigger data-testid="tenant-group-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in_house">In-house company</SelectItem><SelectItem value="client">Client (sold)</SelectItem></SelectContent>
              </Select>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="tenant-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Basic">Basic — ₹999/mo</SelectItem><SelectItem value="Pro">Pro — ₹2,499/mo</SelectItem><SelectItem value="Enterprise">Enterprise — ₹4,999/mo</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.industry_type} onValueChange={(v) => setForm({ ...form, industry_type: v, template: "" })}>
                <SelectTrigger data-testid="tenant-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="service">Service industry</SelectItem><SelectItem value="product">Product industry</SelectItem></SelectContent>
              </Select>
              <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
                <SelectTrigger data-testid="tenant-template-select"><SelectValue placeholder="Industry preset" /></SelectTrigger>
                <SelectContent>{templates.filter((t) => t.industry_type === form.industry_type).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}{t.builtin ? "" : " (custom)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Brand color</label>
                <input type="color" data-testid="tenant-color-input" value={form.brand_color} onChange={(e) => {
                  if (isUnavailableBrandColor(e.target.value)) setColorUnavailable(true);
                  else { setColorUnavailable(false); setForm({ ...form, brand_color: e.target.value }); }
                }} className="w-full h-10 rounded-lg border cursor-pointer mt-1" />
                {colorUnavailable && <p role="alert" className="mt-1 text-xs text-danger-foreground">{"\u0047reen is not available in this theme"}</p>}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Logo URL (optional)</label>
                <Input data-testid="tenant-logo-input" placeholder="https://…/logo.png" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Owner account</div>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="tenant-owner-name" placeholder="Owner name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} required />
                <Input data-testid="tenant-owner-phone" placeholder="Owner phone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} />
              </div>
              <Input data-testid="tenant-owner-email" type="email" placeholder="Owner email (login ID)" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} required />
              <div className="flex gap-2">
                <Input data-testid="tenant-owner-password" placeholder="Password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} required minLength={6} />
                <Button type="button" variant="outline" data-testid="tenant-autogen-pw" onClick={() => setForm({ ...form, owner_password: genPassword() })}><Wand2 size={14} className="mr-1" /> Auto</Button>
              </div>
              {form.owner_password && <div className="text-xs font-mono bg-muted rounded-md px-3 py-2" data-testid="tenant-pw-preview">Login: {form.owner_email || "—"} / {form.owner_password}</div>}
            </div>
            <Button data-testid="tenant-create-submit" type="submit" className="w-full bg-info hover:bg-info">Create Workspace</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOpen} onOpenChange={() => setEditOpen(null)}>
        <DialogContent data-testid="edit-tenant-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit {editOpen?.name}</DialogTitle></DialogHeader>
          {editOpen && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Plan</label>
                <Select value={editOpen.plan} onValueChange={(v) => setEditOpen({ ...editOpen, plan: v })}>
                  <SelectTrigger data-testid="edit-plan-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Basic">Basic</SelectItem><SelectItem value="Pro">Pro</SelectItem><SelectItem value="Enterprise">Enterprise</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Brand color</label>
                <input type="color" data-testid="edit-color-input" value={editOpen.brand_color || themeInputColor("--primary-input-hex")} onChange={(e) => {
                  if (isUnavailableBrandColor(e.target.value)) setColorUnavailable(true);
                  else { setColorUnavailable(false); setEditOpen({ ...editOpen, brand_color: e.target.value }); }
                }} className="w-full h-10 rounded-lg border cursor-pointer mt-1" />
                {colorUnavailable && <p role="alert" className="mt-1 text-xs text-danger-foreground">{"\u0047reen is not available in this theme"}</p>}
              </div>
              <Button data-testid="edit-save-button" type="submit" className="w-full bg-info hover:bg-info">Save Changes</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delOpen} onOpenChange={() => setDelOpen(null)}>
        <DialogContent data-testid="delete-tenant-dialog">
          <DialogHeader><DialogTitle className="font-heading">Delete {delOpen?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The workspace moves to the <b>recycle bin for 30 days</b> — logins are blocked but data stays intact and can be restored anytime. Nothing is permanently erased yet.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDelOpen(null)}>Cancel</Button>
            <Button data-testid="delete-confirm-button" onClick={softDelete} className="bg-danger hover:bg-danger">Move to Recycle Bin</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!permOpen} onOpenChange={() => setPermOpen(null)}>
        <DialogContent data-testid="perm-delete-dialog">
          <DialogHeader><DialogTitle className="font-heading text-danger-foreground">Permanently delete {permOpen?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This erases <b>only this company's</b> data forever. A full ZIP archive is automatically saved to backups first. Other companies are never touched.</p>
            <Input data-testid="perm-delete-name-input" placeholder={`Type "${permOpen?.name}" to confirm`} value={permName} onChange={(e) => setPermName(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPermOpen(null)}>Cancel</Button>
              <Button data-testid="perm-delete-confirm" disabled={permName !== permOpen?.name} onClick={permanentDelete} className="bg-danger hover:bg-danger">Delete Forever</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cloneOpen} onOpenChange={() => setCloneOpen(null)}>
        <DialogContent data-testid="clone-config-dialog">
          <DialogHeader><DialogTitle className="font-heading">Duplicate settings from {cloneOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Copies modules, fields, pipelines, theme, templates & automations. Business data is never copied.</p>
            <Select value={cloneTarget} onValueChange={setCloneTarget}>
              <SelectTrigger data-testid="clone-target-select"><SelectValue placeholder="Target workspace…" /></SelectTrigger>
              <SelectContent>{workspaces.filter((t) => t.slug !== cloneOpen?.slug && t.status !== "deleted").map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="clone-config-submit" disabled={!cloneTarget} onClick={cloneConfig} className="w-full bg-info hover:bg-info">Duplicate Configuration</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
